import {
  horizontalLinesPlugin,
  hoverHighlightPlugin,
  hoverPointPlugin,
  lineShadowPlugin,
  getReportingTooltipYAlign,
  reportingTooltipCaretPadding,
  verticalLinesPlugin,
} from "@/components/reporting/chartPlugins.ts";
import { getHSLAColor } from "@/helpers/style.helper.ts";
import { Box } from "@skailer-com/uikit";
import {
  CategoryScale,
  Chart as ChartJS,
  type ChartData,
  ChartDataset,
  type ChartOptions,
  Filler,
  Legend,
  LinearScale,
  LogarithmicScale,
  LineController,
  LineElement,
  PointElement,
  Title as TitleChart,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import React, { useMemo, useRef } from "react";
import { Chart } from "react-chartjs-2";
import { useTranslation } from "react-i18next";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  PointElement,
  LineElement,
  TitleChart,
  Tooltip,
  Legend,
  LineController,
  ChartDataLabels,
  Filler
);

export interface LineChartSeries {
  data: number[];
  color?: string;
  fill?: boolean;
  label?: string;
  formatValue?: (value: number) => string;
}

export interface LineChartProps {
  labels: string[];
  series: LineChartSeries[];
  height?: number;
  formatTooltipValue?: (value: number) => string;
  formatAxisValue?: (value: number) => string;
  yScale?: "linear" | "logarithmic";
  beginAtZero?: boolean;
  yMax?: number;
  onPointClick?: (seriesIndex: number, pointIndex: number) => void;
}

const niceStep = (rough: number) => {
  const pow = Math.pow(10, Math.floor(Math.log10(rough)));
  const frac = rough / pow;
  const niceFrac = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 5 ? 5 : 10;
  return niceFrac * pow;
};

const buildLinearTicks = (lo: number, hi: number) => {
  const step = niceStep((hi - lo) / 4) || 1;
  const start = Math.floor(lo / step) * step;
  const end = Math.ceil(hi / step) * step + step;
  const ticks: { value: number }[] = [];
  for (let value = start; value <= end + step / 2; value += step) {
    if (value > 0) ticks.push({ value: Math.round(value) });
  }
  return ticks;
};

const buildLogTicks = (dataMin: number, dataMax: number) => {
  const lo = Math.max(dataMin, 1);
  const hi = Math.max(dataMax, lo);
  if (Math.log10(hi / lo) < 1) return buildLinearTicks(lo, hi);
  const all: number[] = [];
  for (let exp = Math.floor(Math.log10(lo)); exp <= Math.ceil(Math.log10(hi)) + 1; exp++) {
    for (const mantissa of [1, 2, 5]) all.push(mantissa * Math.pow(10, exp));
  }
  const loBound = [...all].reverse().find((value) => value <= lo) ?? all[0];
  const hiIndex = all.findIndex((value) => value >= hi);
  const hiBound = all[hiIndex + 1] ?? all[all.length - 1];
  return all.filter((value) => value >= loBound && value <= hiBound).map((value) => ({ value }));
};

const darkGradient = (context: { chart: ChartJS }) => {
  const { ctx, chartArea } = context.chart;
  if (!chartArea) return "transparent";
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  gradient.addColorStop(0, "rgba(37, 39, 43, 0.2)");
  gradient.addColorStop(1, "rgba(37, 39, 43, 0)");
  return gradient;
};

export const LineChart = ({
  labels,
  series,
  height = 236,
  formatTooltipValue,
  formatAxisValue,
  yScale = "linear",
  beginAtZero,
  yMax,
  onPointClick,
}: LineChartProps) => {
  const { t } = useTranslation();
  const chartRef = useRef<ChartJS<"line">>(null);

  // Точки линии нулевого радиуса: ищем ближайший индекс по оси X, а не пересечение
  const handleClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!chartRef.current || !onPointClick) return;
    const elements = chartRef.current.getElementsAtEventForMode(
      event.nativeEvent,
      "index",
      { intersect: false },
      true
    );
    if (elements.length > 0) {
      const { datasetIndex, index } = elements[0];
      onPointClick(datasetIndex, index);
    }
  };

  const isLog = yScale === "logarithmic" && series.every((s) => s.data.every((value) => value > 0));

  const formatValue =
    formatTooltipValue ?? ((value: number) => t("plurals:people", { count: value }));

  const formatAxisTick =
    formatAxisValue ?? formatTooltipValue ?? ((value: number) => `${Number(value.toFixed(2))}`);

  const logTicks = useMemo(() => {
    if (!isLog) return undefined;
    const positives = series.flatMap((s) => s.data).filter((value) => value > 0);
    if (!positives.length) return undefined;
    return buildLogTicks(Math.min(...positives), Math.max(...positives));
  }, [series, isLog]);

  const logMin = logTicks?.[0]?.value;
  const logMax = logTicks?.[logTicks.length - 1]?.value;

  const data = useMemo<ChartData<"line">>(() => {
    const datasets: ChartDataset<"line">[] = series.map((s) => {
      const fill = s.fill ?? true;
      return {
        type: "line",
        label: s.label,
        data: s.data,
        borderColor: s.color ?? "#1B1D21",
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0.4,
        fill,
        backgroundColor: fill ? darkGradient : "transparent",
      };
    });
    return { labels, datasets };
  }, [labels, series]);

  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 250,
    },
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      datalabels: {
        display: false,
      },
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: true,
        mode: "index",
        intersect: false,
        position: "nearest",
        yAlign: getReportingTooltipYAlign,
        caretPadding: reportingTooltipCaretPadding,
        displayColors: false,
        backgroundColor: "#25272B",
        padding: 8,
        cornerRadius: 8,
        titleFont: {
          size: 12,
          weight: "normal",
        },
        bodyFont: {
          size: 12,
          weight: "bold",
        },
        callbacks: {
          title: (context) => {
            return context[0]?.label || "";
          },
          label: (context) => {
            return (series[context.datasetIndex]?.formatValue ?? formatValue)(
              context.raw as number
            );
          },
        },
      },
    },
    scales: {
      x: {
        offset: true,
        grid: {
          display: false,
        },
        ticks: {
          padding: 8,
        },
        border: {
          display: true,
          color: getHSLAColor("--color-dark", 0.1),
        },
      },
      y: {
        type: isLog ? "logarithmic" : "linear",
        display: true,
        position: "right",
        min: isLog ? logMin : undefined,
        max: isLog ? logMax : undefined,
        beginAtZero: isLog ? undefined : beginAtZero,
        grace: isLog ? undefined : "25%",
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        afterBuildTicks: isLog
          ? (axis) => {
              if (logTicks?.length) axis.ticks = logTicks;
            }
          : yMax != null
            ? (axis) => {
                axis.ticks = axis.ticks.filter((tick) => tick.value <= yMax);
              }
            : undefined,
        ticks: {
          padding: 8,
          autoSkip: false,
          maxTicksLimit: isLog ? undefined : 6,
          callback: (value) => formatAxisTick(Number(value)),
        },
      },
    },
  };

  return (
    <Box style={{ height }}>
      <Chart
        ref={chartRef}
        className={onPointClick ? "cursor-pointer" : undefined}
        onClick={onPointClick ? handleClick : undefined}
        type={"line"}
        options={options}
        data={data}
        plugins={[
          horizontalLinesPlugin,
          hoverHighlightPlugin,
          lineShadowPlugin,
          verticalLinesPlugin,
          hoverPointPlugin,
        ]}
      />
    </Box>
  );
};
